import React, { createContext, useContext, useState, useCallback } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaExclamationTriangle, FaTimes } from 'react-icons/fa';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const Toast = ({ id, type, message, onClose }) => {
  const config = {
    success: {
      icon: <FaCheckCircle className="w-5 h-5" />,
      bg: 'bg-gradient-to-r from-emerald-500 to-green-600',
      border: 'border-l-4 border-emerald-300',
      iconBg: 'bg-white/20',
    },
    error: {
      icon: <FaExclamationCircle className="w-5 h-5" />,
      bg: 'bg-gradient-to-r from-red-500 to-rose-600',
      border: 'border-l-4 border-red-300',
      iconBg: 'bg-white/20',
    },
    warning: {
      icon: <FaExclamationTriangle className="w-5 h-5" />,
      bg: 'bg-gradient-to-r from-secondary-500 to-amber-500',
      border: 'border-l-4 border-secondary-400',
      iconBg: 'bg-white/20',
    },
    info: {
      icon: <FaInfoCircle className="w-5 h-5" />,
      bg: 'bg-gradient-to-r from-primary-500 to-primary-700',
      border: 'border-l-4 border-primary-300',
      iconBg: 'bg-white/20',
    },
  };

  const style = config[type] || config.info;

  return (
    <div
      className={`
        ${style.bg} ${style.border}
        flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl
        text-white shadow-3d backdrop-blur-md
        transform transition-all duration-300 ease-out
        animate-slide-in-right
        min-w-[300px] max-w-md
        border border-white/10
      `}
    >
      <div className="flex items-center gap-3">
        <div className={`flex-shrink-0 p-1.5 rounded-lg ${style.iconBg}`}>
          {style.icon}
        </div>
        <p className="text-sm font-medium leading-snug">{message}</p>
      </div>
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/20 transition-colors duration-200"
        aria-label="Dismiss notification"
      >
        <FaTimes className="w-4 h-4" />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((type, message, duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, message }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const success = useCallback((message, duration) => addToast('success', message, duration), [addToast]);
  const error = useCallback((message, duration) => addToast('error', message, duration), [addToast]);
  const warning = useCallback((message, duration) => addToast('warning', message, duration), [addToast]);
  const info = useCallback((message, duration) => addToast('info', message, duration), [addToast]);

  const value = {
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-16 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              id={toast.id}
              type={toast.type}
              message={toast.message}
              onClose={removeToast}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;
